import { __decorate, __metadata, __awaiter } from 'tslib';
import 'reflect-metadata';

/**
 * Error types for Darwin modules
 */
var ErrorTypes;
(function (ErrorTypes) {
    ErrorTypes["DW_ERROR"] = "DwError";
    ErrorTypes["DW_FETCH"] = "DwFetchError";
    ErrorTypes["DW_CONFIG_PARSER"] = "DwConfigParserError";
    ErrorTypes["DW_CONFIGURABLE"] = "DwConfigurableError";
    ErrorTypes["DW_HEADERABLE"] = "DwHeaderableError";
    ErrorTypes["DW_EMITTER"] = "DwEmitterError";
    ErrorTypes["DW_VALIDATORS"] = "DwValidatorsError";
})(ErrorTypes || (ErrorTypes = {}));

/**
 * Extends from error and add a property code error
 */
class DwError extends Error {
    /**
     * Instance DarwinError class
     * @param message error message
     * @param name error name. Default 'Error'. Ej: err.toString() => 'Error:  ...'
     * @param code error code
     */
    constructor(message, name = ErrorTypes.DW_ERROR, code) {
        super(message);
        this.name = name;
        this.code = code;
    }
}

/**
 * Error codes for validatable
 */
var ValidatableErrorCodes;
(function (ValidatableErrorCodes) {
    ValidatableErrorCodes[ValidatableErrorCodes["DW_INVALID_REGISTER_VALIDATOR_PARAMETERS"] = 1701] = "DW_INVALID_REGISTER_VALIDATOR_PARAMETERS";
    ValidatableErrorCodes[ValidatableErrorCodes["DW_INVALID_REGISTER"] = 1702] = "DW_INVALID_REGISTER";
    ValidatableErrorCodes[ValidatableErrorCodes["DW_INVALID_PROPS"] = 1703] = "DW_INVALID_PROPS";
    ValidatableErrorCodes[ValidatableErrorCodes["DW_INVALID_REGEXP"] = 1704] = "DW_INVALID_REGEXP";
    ValidatableErrorCodes[ValidatableErrorCodes["DW_INVALID_PARAMETER_TYPE"] = 1705] = "DW_INVALID_PARAMETER_TYPE";
})(ValidatableErrorCodes || (ValidatableErrorCodes = {}));

function allPass(...args) {
    const fnList = args[0] && args[0][Symbol.iterator] ? args[0] : args;
    return (value) => fnList.every((fn) => fn(value));
}

/**
 * Returns a function that always returns the given value. Note that for
 * non-primitives the value returned is a reference to the original value.
 * @param arg The value to wrap in a function
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const t = R.always('Tee');
 *
 * t(); //=> 'Tee'
 * ```
 */
function always(arg) {
    return () => arg;
}

function anyPass(...args) {
    const fnList = args[0] && args[0][Symbol.iterator] ? args[0] : args;
    return (value) => fnList.some((fn) => fn(value));
}

function curry(fn) {
    return function currify(...a) {
        const args = Array.prototype.slice.call(arguments);
        return args.length >= fn.length ?
            fn.apply(null, args) :
            currify.bind(null, ...args);
    };
}

/**
 * Sets a property to an object, It maintain the instance methods BUT also mutates it.
 * Use assoc Function if you want to have a new object with no reference anywhere.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const testObject = {
 *  a: 'a'
 * };
 *
 * const result = assoc('b', 'b', testObject);
 * /// { a: 'a', b: 'b'}
 * ```
 */
const assocMutate = curry((prop, value, obj) => {
    obj[prop] = value;
    return obj;
});

/**
 * Sets a property to an object, In case of an instance, it loses all methods.
 * Use assocMutate to preserve methods.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const testObject = {
 *  a: 'a'
 * };
 * const result = assoc('b', 'b', testObject);
 * /// { a: 'a', b: 'b'}
 * ```
 */
const assoc = curry((prop, val, obj) => {
    const result = {};
    for (const p in obj) {
        result[p] = obj[p];
    }
    assocMutate(prop, val, result);
    return result;
});

function both(predA, predB) {
    return (...value) => predA(...value) && predB(...value);
}

/**
 * Function that returns the `!` of its argument. It will return `true` when
 * passed false-y value, and `false` when passed a truth-y one.
 * @param arg any
 * @returns boolean
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * not(true); //=> false
 * not(false); //=> true
 * not(0); //=> true
 * not(1); //=> false
 * ```
 */
function not(arg) {
    return !arg;
}

function complement(pred) {
    return (...args) => not(pred(...args));
}

/**
 * Compares an argument with each condition
 * from a condition's list until one of them is met.
 *
 * If the the result of the predicate is truthy, then it returns the
 * execution from the transformer function within the pairs,
 * otherwise it returns undefined.
 * @usageNotes
 * How to use it.
 * ### Example 1
 * ``` ts
 * const nameNumber = cond([
 *   [(a:any) => a === 0, () => 'zero'],
 *   [(a:any) => a === 1, () => 'one'],
 *   [(a:any) => a === 2, () => 'two'],
 *   [(a:any) => a === 3, (a) => a],
 * ]);
 *
 * nameNumber(0) // zero
 * nameNumber(1) // one
 * nameNumber(2) // two
 * nameNumber('anything') // undefined
 * nameNumber(3) // 3
 * ```
 * ### Example 2
 * ``` ts
 * const nameNumber = cond([
 *   const T = (x:any) => true;
 *   const nameNumber = cond([
 *     [(a:any) => a === 0, () => 'zero'],
 *     [(a:any) => a === 1, () => 'one'],
 *     [(a:any) => a === 2, () => 'two'],
 *     [T, () => 'I don´t know']
 *   ]);
 *
 * nameNumber(0) // zero
 * nameNumber(1) // one
 * nameNumber(2) // two
 * nameNumber('anything') // I don't know
 * ```
 */
const cond = curry((pairs, value) => {
    const pair = pairs.find(predicateIsTruthy(value));
    if (pair != null) {
        return pair[1](value);
    }
});
const predicateIsTruthy = (value) => (possiblePair) => possiblePair[0](value);

/**
 * Identity function. It's output it's the input received.
 *
 * F(x) = x
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * identity(3); // 3
 * identity({}); // {}
 * ```
 */
const identity = (x) => x;

/**
 * Function that will process either the onTrue or the onFalse
 * function depending upon the result of the condition predicate.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const isEven = (n:number) => n % 2 === 0;
 * const isOdd = (n:number) => Math.abs(n % 2) === 1;
 * const even = () => 'It is an even number';
 * const odd = () => 'It is an odd number';
 *
 * const fn = ifElse(
 *  isEven, even, odd
 * );
 *
 * fn(1)  // 'It is an odd number'
 * fn(4)  // 'It is an even number'
 * ```
 */
const ifElse = curry((pred, whenTrueFn, whenFalseFn, value) => pred(value) ? whenTrueFn(value) : whenFalseFn(value));

/**
 * Compares two predicates against the third argument. Equivalent to OR (||)
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const falsy = () => false;
 * const truthy = () => true;
 *
 * either(falsy,truthy)('test') // true
 * either(truthy,truthy)('test') // true
 * either(falsy,falsy)('test') // false
 * ```
 */
const either = curry((predA, predB, value) => (predA(value) || predB(value)));

function pipe(...fns) {
    return fns.reduce((accum, fn) => (...args) => fn(accum(...args)));
}

/**
 * Placeholder to use in gt, gte, lt and lte predicates
 * It is used together with the curry to pass a second parameter to a function before the first one
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * lt(__, 2)(1) //=> true
 * ```
 */
const __ = Symbol('__');

/**
 * Checks if an argument is null or undefined
 * @param arg any
 * @returns boolean
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * isNil(undefined); // => true
 * isNil(null); // => true
 * isNil(function() { }); // => false
 * isNil(''); // => false
 * isNil(0); // => false
 * ```
 */
function isNil(arg) {
    return arg == null;
}

// tslint:disable:variable-name
const _lt = curry((n1, n2) => n1 < n2);
const _ltPH = (n2) => (n1) => _lt(n1, n2);
function lt(...args) {
    if (args[0] === __) {
        return _ltPH(args[1]);
    }
    return isNil(args[1]) ? _lt(args[0]) : _lt(args[0], args[1]);
}

/**
 * Checks if an argument is type of Array
 * @param arg any
 * @returns boolean
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * isArray(''); // => false
 * isArray(0); // => false
 * isArray([]); // => true
 * ```
 */
function isArray(arg) {
    return Array.isArray(arg);
}

/**
 * Returns the first `n` elements of the given list, string, or
 * transducer/transformer (or object with a `take` method).
 * @param n Number of elements to take
 * @param list
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * take(1, 'abx'); // => 'a'
 * take(1, ['ab','bc']); // => ['ab']
 * take(-1, 'abx'); => 'abx'
 * ```
 */
const take = curry((n, list) => {
    const chunk = Array.prototype.slice.call(list, 0, lt(n, 0) ? Infinity : n);
    return isArray(list) ? chunk : chunk.join('');
});

/**
 * Runs a function with supplied value and returns the value afterwards.
 * Its Best used for composition, to execute side effect logic.
 *
 * @usageNotes
 * How to use it.
 *
 * ### Example
 * ``` ts
 * const sayX = x => console.log('x is ' + x);
 * tap(sayX, 100); //=> 100
 *
 * // logs 'x is 100'
 * // Its Best used for composition, to execute side effect logic.
 *
 * pipe(
 *   tap(...),
 *   tap(...)
 * )
 * ```
 */
const tap = curry((fn, value) => {
    fn(value);
    return value;
});

/**
 * Function which takes a label and returns a function which takes a value of any type
 * and prints to console the following format <label> : <value> before returning the value.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const sum3 = (a:number) => a + 3;
 * const sum2 = (a:number) => a + 2;
 * const sum5 = pipe(
 *  trace('Before transforming'),
 *  sum3,
 *  trace('After sum3'),
 *  sum2,
 *  trace('After sum3 & sum 2')
 * );
 *
 * sum5(3);
 * // Before transforming : 3
 * // After sum3 : 6
 * // After sum3 & sum2 : 8
 * ```
 */
const trace = (label) => (value) => {
    console.log(`${label} : ${value}`);
    return value;
};

/**
 * Tests the final argument by passing it to the given predicate function.
 * - If the predicate is not satisfied, the function will return the result
 * of calling the whenFalseFn function with the same argument.
 * - If the predicate is satisfied, the argument is returned as is.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const truthy = () => true;
 * const falsy = () => false;
 *
 * unless(falsy,truthy,false) // true
 * unless(truthy,truthy,false) // false
 * unless(falsy,falsy,true) // false
 * ```
 */
const unless = curry((pred, whenFalseFn, x) => (pred(x) ? x : whenFalseFn(x)));

/**
 * Tests the final argument by passing it to the given predicate function.
 * - If the predicate is satisfied,the function will return
 * the result of calling the whenTrueFn function with the same argument.
 * - If the predicate is not satisfied, the argument is returned as is.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const truthy = () => true;
 * const falsy = () => false;
 *
 * when(truthy, truthy, false) // true
 * when(truthy, truthy, true) // true
 * when(falsy, truthy, false) // false
 * when(truthy, falsy, true) // false
 * ```
 */
const when = curry((pred, whenTrueFn, x) => (pred(x) ? whenTrueFn(x) : x));

/**
 * Takes a spec object and a object to validate,
 * returns true if the test satisfies the spec and false otherwise.
 *
 * Each predicate is applied to the value of the corresponding property of the object to validate.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const fn= where({
 *  a: (a: any) => a === 'a',
 *  b: (b: any) => b !== 'b',
 *  c: (c: any) => c > 10,
 *  d: (d: any) => d < 20
 *})
 *
 * fn({a: 'a', b: 'xxx', x: 11, y: 19});    //  true
 * fn({a: 'a', b: 'b', x: 11, y: 19});      //  false
 * fn({a: 'xxx', b: 'xxx', x: 11, y: 19});  //  false
 * fn({a: 'a', b: 'xxx', x: 10, y: 19});    //  false
 * fn({a: 'a', b: 'xxx', x: 11, y: 20});    //  false
 * ```
*/
const where = curry((spec, objToValidate) => {
    for (const prop in spec) {
        if (!spec[prop](objToValidate[prop])) {
            return false;
        }
    }
    return true;
});

/**
 * Compares two booleans. Equivalent to (&&)
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * and(false,true)('test') // false
 * and(true,true)('test') // true
 * and(false,false)('test') // false
 * ```
 */
const and = curry((a, b) => a && b);

/**
 * Its a function that always return 'false'.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * F(); //  false
 * ```
 */
const F = always(false);

// tslint:disable:variable-name
const _gt = curry((n1, n2) => n1 > n2);
const _gtPH = (n2) => (n1) => _gt(n1, n2);
function gt(...args) {
    if (args[0] === __) {
        return _gtPH(args[1]);
    }
    return isNil(args[1]) ? _gt(args[0]) : _gt(args[0], args[1]);
}

// tslint:disable:variable-name
const _gte = curry((n1, n2) => n1 >= n2);
const _gtePH = (n2) => (n1) => _gte(n1, n2);
function gte(...args) {
    if (args[0] === __) {
        return _gtePH(args[1]);
    }
    return isNil(args[1]) ? _gte(args[0]) : _gte(args[0], args[1]);
}

/**
 * Checks if an argument is instance of a valid Date
 * @param arg any
 * @returns boolean
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * isDate(''); // => false
 * isDate(0); // => false
 * isDate(new Date()); // => true
 * isDate(new Date('wrong date')); // => false
 * ```
 */
function isDate(arg) {
    return allPass(complement(isNil), complement(isNaN), arg => arg instanceof Date)(arg);
}

/**
 * Checks if an object is a primitive type (string, number, boolean, null, undefined or symbol)
 * @param arg any
 * @returns boolean
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * isPrimitive('str'); // => true
 * isPrimitive(0); // => true
 * isPrimitive(null); // => true
 * isPrimitive(undefined); // => true
 * isPrimitive({}); // => false
 * isPrimitive(function(){}); // => false
 * ```
 */
function isPrimitive(arg) {
    const type = typeof arg;
    return arg == null || (type !== 'object' && type !== 'function');
}

const arrayObjects = '[object Object]';
// tslint:disable-next-line:function-name
function _equals(a, b) {
    if (isPrimitive(a) && isPrimitive(b)) {
        return compareItem(a, b);
    }
    if (isArray(a) && isArray(b)) {
        return equalsArray(a, b);
    }
    if (Object.prototype.toString.call(a) === arrayObjects
        && Object.prototype.toString.call(b) === arrayObjects) {
        return equalsObject(a, b);
    }
    return false;
}
function equalsArray(a, b) {
    const lengthA = a.length;
    const lengthB = b.length;
    if (lengthA !== lengthB) {
        return false;
    }
    for (let i = 0; i < lengthA; i += 1) {
        if (!compareItem(a[i], b[i])) {
            return false;
        }
    }
    return true;
}
function equalsObject(a, b) {
    const lengthA = Object.keys(a).length;
    const lengthB = Object.keys(b).length;
    if (lengthA !== lengthB) {
        return false;
    }
    const keys = Object.keys(a);
    for (let i = 0, l = keys.length; i < l; i += 1) {
        const key = keys[i];
        // @ts-ignore
        if (!compareItem(a[key], b[key])) {
            return false;
        }
    }
    return true;
}
function compareItem(item1, item2) {
    const item1Type = Object.prototype.toString.call(item1);
    if (isArray(item1)) {
        return equalsArray(item1, item2);
    }
    if (item1Type === arrayObjects) {
        return equalsObject(item1, item2);
    }
    if (item1Type !== Object.prototype.toString.call(item2)) {
        return false;
    }
    if (item1Type === '[object Function]'
        && item1.toString() === item2.toString()) {
        return true;
    }
    if (item1 !== item2) {
        return false;
    }
    return true;
}
/**
 * Function to compare two arrays or two objects or two primitive types
 * The object can have primitives types, arrays, or other objects
 * The array must only have primitive types (symbol type exclude) and objects
 * @param t1 object | any[]
 * @param t2 object | any[]
 * @returns boolean
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * equals([1, 2], [1, 2]) // true
 * equals({}, {}) // true
 * equals({ a: null }, { a: null }) // true
 * equals(
 *    {
 *      a: 1,
 *      b: { c: 5 },
 *      c: [1, 2],
 *      d: { e: [{ a:'str' }, true] },
 *      f: { g: { h: null }, i: undefined }
 *    },
 *    {
 *      a: 1,
 *      b: { c: 5 },
 *      c: [1, 2],
 *      d: { e: [{ a:'str' }, true] },
 *      f: { g: { h: null }, i: undefined }
 *    }
 *  ) // true
 * ```
 */
const equals = curry(_equals);

const hasSameLength = curry((length, arg) => equals(arg.length, length));
const isPlainObject = (arg) => arg.constructor === Object;
// tslint:disable-next-line:function-name
function _isEmpty(arg) {
    if (isNil(arg)) {
        return true;
    }
    const typeofO = typeof arg;
    if (anyPass(equals('boolean'), equals('number'), equals('function'))(typeofO)) {
        return false;
    }
    if (equals('string', typeofO)) {
        return equals(arg, '');
    }
    // if is array and length is 0 is empty
    if (allPass(isArray, hasSameLength(0))(arg)) {
        return true;
    }
    // if o is a plain object and not have properties is empty
    if (allPass(() => equals('object', typeofO), isPlainObject, o => hasSameLength(0, Object.keys(o)))(arg)) {
        return true;
    }
    return false;
}
/**
 * Check if an object is empty, null or not defined
 * @param arg Object to check
 * @returns `true` if is empty, `false` otherwise
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * isEmpty(''); // => true
 * isEmpty('a'); // => false
 * isEmpty([]); // => true
 * isEmpty(['a']); // => false
 * isEmpty(undefined); // => true
 * isEmpty(null); // => true
 * isEmpty(0); // => false
 * isEmpty(true); // => false
 * isEmpty({}); // => true
 * isEmpty({ a: 'a' }); // => false
 * ```
 */
const isEmpty = _isEmpty;

/**
 * Checks if an argument is type of function
 * @param arg any
 * @return boolean
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * isFunction(''); // => false
 * isFunction(0); // => false
 * isFunction(() => {}); // => true
 * isFunction(function() {}) // => true
 * ```
 */
function isFunction(arg) {
    return typeof arg === 'function';
}

// tslint:disable:variable-name
const _lte = curry((n1, n2) => n1 <= n2);
const _ltePH = (n2) => (n1) => _lte(n1, n2);
function lte(...args) {
    if (args[0] === __) {
        return _ltePH(args[1]);
    }
    return isNil(args[1]) ? _lte(args[0]) : _lte(args[0], args[1]);
}

// tslint:disable-next-line:function-name
function _isTypeOf(type, arg) {
    return equals(typeof arg, type);
}
/**
 * Compare if an object is type of a javascript type
 * @param type javascript types
 * @param arg object to compare
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * isTypeOf('string', 'a'); // => true
 * isTypeOf('object')({}); // => true
 * isTypeOf('string', 0); // => false
 * isTypeOf('number', 0); // => true
 * ```
 */
const isTypeOf = curry(_isTypeOf);

// tslint:disable-next-line:function-name
function _eqProps(prop, 
// @ts-ignore
{ [prop]: val1 }, 
// @ts-ignore
{ [prop]: val2 }) {
    return isPrimitive(val1) && isPrimitive(val2)
        ? val1 === val2
        : equals(val1, val2);
}
/**
 * Reports whether two objects have the same value, in R.equals terms,
 * for the specified property. Useful as a curried predicate.
 * The object can only have as values: primitives types, arrays, or other objects
 * @param prop string
 * @param obj1 object
 * @param obj2 object
 * @returns boolean
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * eqProps('p', { p: 1 }, { p: 1 }) // true;
 * eqProps('p', { p: [1, 2] }, { p: [1, 2] })
 * eqProps('p', { p: { q: 5 } }, { p: { q: 5 } })
 * ```
 */
const eqProps = curry(_eqProps);

/**
 * Compares two booleans. Equivalent to (||)
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * or(false,true)('test') // true
 * or(true,true)('test') // true
 * or(false,false)('test') // false
 * ```
 */
const or = curry((a, b) => a || b);

/**
 * Its a function that always return 'true'.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * T(); // true
 * ```
 */
const T = always(true);

/**
 * Takes a spec object and an object to validate; returns true if the object to validate satisfies
 * the spec, false otherwise.
 *
 * Each property's value  from the Spec object it's wrapped within an _equals_ predicate.
 *
 * _whereEq_ is a specialization of _where_.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const fn= whereEq({
 *  a :'a',
 *  b :'b',
 * });
 *
 * fn({a: 'a'});                  // false
 * fn({a: 'a', b: 'b'});          // true
 * fn({a: 'a', b: 'b', c: 'c'});  // true
 * fn({a: 'a', b: 'a'});          // false
 * ```
 */
const whereEq = curry((spec, objToValidate) => {
    const composedEqualsSpec = Object.entries(spec).reduce(wrapValue, {});
    return where(composedEqualsSpec, objToValidate);
});
const wrapValue = (obj, [key, value]) => {
    obj[key] = equals(value);
    return obj;
};

/**
 * Map for save all the validators
 */
const validators = new Map();
/**
 * Register a new validator in the validators Map
 * @param validatorName Name of the validator (It will be stored as metadata key)
 * @param validatorOptions
 * @param validatorOptions.validationFn validation-function to check if the property is valid
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * registerDecorator({
 *     metadataKey: 'dw:design:string-property',
 *  {
 *    validationFn: ({ property, options }) => isValid: true
 *  }
 * });
 * ```
 */
function registerValidator(validatorName, validatorOptions) {
    const isAnyArgumentNil = anyPass(() => isNil(validatorName), () => isNil(validatorOptions), () => isNil(validatorOptions.decorator), () => isNil(validatorOptions.validationFn));
    if (isAnyArgumentNil()) {
        throw new DwError('To register a validator requires a right name and options', ErrorTypes.DW_VALIDATORS, ValidatableErrorCodes.DW_INVALID_REGISTER_VALIDATOR_PARAMETERS);
    }
    if (validators.has(validatorName)) {
        throw new DwError(`${validatorName} validator can not be registered. A validator can not be re-registered`, ErrorTypes.DW_VALIDATORS, ValidatableErrorCodes.DW_INVALID_REGISTER);
    }
    // if everything is right the validator is registered in the Map
    validators.set(validatorName, validatorOptions);
}

/**
 * Constant that indicates the key name for the required properties metadata
 * It used together requiredProperty decorator
 * @constant {string}
 */
const requiredMetadataKey = 'dw:design:required';

/**
 * Check if a value is empty
 * @param param0 Parameter that contains property data
 */
const requiredValidationFn = function ({ property: { name, value } }) {
    if (isEmpty(value)) {
        return {
            isValid: false,
            errorMessage: `Property ${name} is required. It can not be null, undefined or empty value.`
        };
    }
    return {
        isValid: true,
        errorMessage: ''
    };
};

/**
 * Function to define a metadata on target property
 * @param arg0 info to be stored
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * class Test {
 *   public prop1: string;
 * };
 * defineMetadata({
 *   metadataKey: 'dw:metadata:prop',
 *   metadataValue: 'valueXXX',
 *   target: Test.prototype,
 *   propertyName: 'prop1'
 * );
 * ```
 */
function defineMetadata({ metadataKey, metadataValue, target }) {
    const metadataValueStored = Reflect.getOwnMetadata(metadataKey, target);
    const isNotNil = complement(isNil);
    // if the metadata has already been stored, it is not necessary to define it again
    // just assign the new property
    if (isNotNil(metadataValueStored)) {
        Object.assign(metadataValueStored, metadataValue);
        return;
    }
    Reflect.defineMetadata(metadataKey, Object.assign({}, metadataValue), target);
}

/**
 * Decorator to indicate that a property is mandatory,
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * class Test {
 *   @requiredProperty()
 *   test1: number;
 * }
 * ```
 */
function requiredProperty() {
    return (target, propertyName) => {
        defineMetadata({
            target,
            metadataKey: requiredMetadataKey,
            metadataValue: { [propertyName]: {} }
        });
    };
}

registerValidator(requiredMetadataKey, {
    validationFn: requiredValidationFn,
    decorator: requiredProperty
});

/**
 * Constant that indicates the key name for the boolean properties metadata
 * It used together booleanProperty decorator
 * @constant {string}
 */
const booleanMetadataKey = 'dw:design:boolean';

/**
 * Checks if a property value is a boolean type
 * @param param0 Parameter that contains property data
 */
const booleanValidationFn = function ({ property: { value, name } }) {
    const neiherBooleanNorNil = allPass(complement(isTypeOf('boolean')), complement(isNil));
    if (neiherBooleanNorNil(value)) {
        return {
            isValid: false,
            errorMessage: `Property ${name} isn't a boolean.`
        };
    }
    return {
        isValid: true,
        errorMessage: ''
    };
};

/**
 * Decorator to indicate that a property is of type boolean,
 * The property is verified to match that type
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * class Test {
 *   @booleanProperty,
 *   test1: boolean;
 * }
 * ```
 */
function booleanProperty() {
    return (target, propertyName) => {
        defineMetadata({
            target,
            metadataKey: booleanMetadataKey,
            metadataValue: { [propertyName]: {} },
        });
    };
}

registerValidator(booleanMetadataKey, {
    validationFn: booleanValidationFn,
    decorator: booleanProperty
});

/**
 * Checks that a number matchs a serie of constrains
 * @param param0 Parameter that contains property data and options configuration
 */
const numberValidationFn = function ({ property: { name, value }, options = {} }) {
    const min = options.min;
    const max = options.max;
    const neiherNumberNorNil = allPass(complement(isTypeOf('number')), complement(isNil));
    if (neiherNumberNorNil(value)) {
        return {
            isValid: false,
            errorMessage: `Property ${name} is not of type number.`
        };
    }
    // the property value is checked only if is not null or undefined
    // otherwise the value is valid
    if (not(isNil(value))) {
        const greaterOrEqualConstraint = isNil(min)
            ? always(true)
            : gte(__, min);
        const lowerOrEqualConstraint = isNil(max)
            ? always(true)
            : lte(__, max);
        const matchConstraints = allPass(greaterOrEqualConstraint, lowerOrEqualConstraint);
        if (not(matchConstraints(value))) {
            const errorMsg = `Property ${name} does not match the constrains options`;
            return {
                isValid: false,
                errorMessage: errorMsg
            };
        }
    }
    return {
        isValid: true,
        errorMessage: ''
    };
};

/**
 * Constant that indicates the key name for the number properties metadata
 * It used together numberProperty decorator
 * @constant {string}
 */
const numberMetadataKey = 'dw:design:number';

/**
 * Decorator to indicate that a property is of type number.
 * The property is verified to match that type.
 * @param options NumberValidatorOptions
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const options: NumberOptions = { min: 0 };
 * class Test {
 *   @numberProperty(options)
 *   test1: number;
 * }
 * ```
 */
function numberProperty(options = {}) {
    return (target, propertyName) => {
        defineMetadata({
            target,
            metadataKey: numberMetadataKey,
            metadataValue: { [propertyName]: options },
        });
    };
}

registerValidator(numberMetadataKey, {
    validationFn: numberValidationFn,
    decorator: numberProperty
});

/**
 * Constant that indicates the key name for the string properties metadata
 * It used together stringProperty decorator
 * @constant {string}
 */
const stringMetadataKey = 'dw:design:string';
/**
 * Regular expression for an email
 */
const emailRegExp = 
// tslint:disable-next-line:max-line-length
/^(?:(?:[^<>()\[\]\\.,;:\s@"]+(?:\.[^<>()\[\]\\.,;:\s@"]+)*)|(?:".+"))@(?:(?:\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(?:(?:[a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
/**
 * Regular expression for an URL
 */
const endPointRegExp = 
// tslint:disable-next-line:max-line-length
/^(?:(?:(?:http(?:s)?:\/\/)?(?:\w[\w-]*(?:\.\w[\w-]*)*(?:\.[a-zA-Z0-9]+)|localhost)(?:\:(?:[0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5]))?)?(?:\/[\w-]+)+)$|^(?:(?:http(?:s)?:\/\/)?(?:\w[\w-]*(?:\.\w[\w-]*)*(?:\.[a-zA-Z0-9]+)|localhost)(?:\:(?:[0-9]{1,4}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5]))?(?:\/[\w-]+)*)$/;

/**
 * Built-in types to recognize patterns in strings
 * Used next to StringTypeValidatorOptions
 */
var StringTypeValidator;
(function (StringTypeValidator) {
    StringTypeValidator["EMAIL"] = "email";
    StringTypeValidator["ENDPOINT"] = "endpoint";
})(StringTypeValidator || (StringTypeValidator = {}));

/**
 * Checks that a string matchs a serie of constrains
 * @param param0 Parameter that contains property data and options configuration
 */
const stringValidationFn = function ({ property: { name, value }, options }) {
    const neiherStringNorNil = allPass(complement(isNil), complement(isTypeOf('string')));
    if (neiherStringNorNil(value)) {
        return {
            isValid: false,
            errorMessage: `Property ${name} is not of type string.`
        };
    }
    if (not(isNil(value))) {
        // Rule to check if a string property matchs a built-in regular expression
        const typeConstraint = isNil(options.type)
            ? always(true)
            : (value) => getRegExp(options.type).test(value);
        // Rule to check if the max string length match the constraint
        const greaterOrEqualConstraint = isNil(options.minLength)
            ? always(true)
            : (value) => gte(value.length, options.minLength);
        // Rule to check if the min string length match the constraint
        const lowerOrEqualConstraint = isNil(options.maxLength)
            ? always(true)
            : (value) => lte(value.length, options.maxLength);
        // Constraints union
        const allButRegexpConstraint = allPass(typeConstraint, greaterOrEqualConstraint, lowerOrEqualConstraint);
        // Rule to check if the string value match the RegExp constraint
        const regexpConstraint = (value) => {
            const regExp = options.regExp;
            if (Object.prototype.toString.call(regExp) !== '[object RegExp]') {
                throw new DwError(`A RegExp was expected but ${typeof regExp} was found`, ErrorTypes.DW_VALIDATORS, ValidatableErrorCodes.DW_INVALID_REGEXP);
            }
            return regExp.test(value);
        };
        // if regeExp is not empty is the only constrint that matters
        // otherwise the others constraints has to be checked
        const matchConstraints = isNil(options.regExp)
            ? allButRegexpConstraint
            : regexpConstraint;
        if (not(matchConstraints(value))) {
            const errorMsg = `Property ${name} does not match the constrains options`;
            return {
                isValid: false,
                errorMessage: errorMsg
            };
        }
    }
    return {
        isValid: true,
        errorMessage: ''
    };
};
/**
 * Gets the regular expresion of a string type
 *
 * @param strType string type to get the regular expresion
 * @returns RegExp return a regexp specific of StringType
 */
const getRegExp = cond([
    [equals(StringTypeValidator.EMAIL), always(emailRegExp)],
    [equals(StringTypeValidator.ENDPOINT), always(endPointRegExp)]
]);

function stringProperty(options = {}) {
    return (target, propertyName) => {
        defineMetadata({
            target,
            metadataKey: stringMetadataKey,
            metadataValue: { [propertyName]: options }
        });
    };
}

registerValidator(stringMetadataKey, {
    validationFn: stringValidationFn,
    decorator: stringProperty
});

/**
 * Mixin with next functionalities:
 * - Automatically validates properties constraints when the class is instantiated
 * - Manual validation with `validate` method
 *
 * **IMPORTANT**: The properties with constraints decorators can not be initialize in the class
 * but only when it is instantiated. The first parameter of the constructor must be an object,
 * the key must be the name of the param, and the key value must be the value of the param
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * class Test extends validatable() {
 *   @numberProperty({ min: 5 })
 *   myProperty!: number;
 * }
 *
 * // constructor fires validation
 * const t = new Test({myProperty: 3}) // => throws
 *
 * // manual validation
 * t.myProperty = 10
 * t.validate() // => not throws
 * ```
 */
// tslint:disable-next-line:variable-name
function validatable(Base = class {
}) {
    return class extends Base {
        constructor(...args) {
            super(args);
            const isNotNil = complement(isNil);
            const isFirstArgPlainObj = (args) => equals('[object Object]', Object.prototype.toString.call(args[0]));
            const constraints = allPass(isNotNil, isFirstArgPlainObj);
            // if args not null and first argument is a plain object then copy properties
            if (constraints(args)) {
                Object.assign(this, args[0]);
            }
            this.validate();
        }
        /**
         * Allows to verify that all the properties match the constrints,
         * if any of then does not match, it throws
         */
        validate() {
            validateFn(this);
        }
    };
}
/**
 * Function to check the properties constraints of an object
 * If any property does not match it throws
 * @param objectInstance instance with metadata constraints.
 * It means, whose properties have constraints
 */
function validateFn(objectInstance) {
    const isNotValid = (item) => !item.isValid;
    const errors = Array.from(validators)
        .flatMap(([metadataKey, { validationFn }]) => validateProps(getMedatata(metadataKey, objectInstance), validationFn, objectInstance))
        .filter(isNotValid)
        .reduce((prev, curr) => {
        return `${prev}${curr.errorMessage}\n`;
    }, '');
    if (errors) {
        throw new DwError(errors, ErrorTypes.DW_VALIDATORS, ValidatableErrorCodes.DW_INVALID_PROPS);
    }
}
/**
 * Function to recover saved metadata about some key
 * It traverses all the prototype chain looking for the data. This is necessary due to
 * the inheritance, as an object can have constrains on both the child class and the base class
 * with the same metadata key
 * @param metadataKey metadata key to look for
 * @param target object where the metadata is saved (or in its prototype chain)
 */
function getMedatata(metadataKey, target) {
    return target.__proto__
        ? Object.assign({}, getMedatata(metadataKey, target.__proto__), Reflect.getOwnMetadata(metadataKey, target.__proto__)) : {};
}
/**
 * Function to retrieve the posible errors of a single validator
 * @param constraints object where key is the property name and value is the constraint options
 * @param validateFn function that knows how to check the constraints and the values
 * @param values objecto where key is de property name and value is the property value
 */
function validateProps(constraints, validateFn, values) {
    return Object
        .entries(constraints)
        .map(([key, options]) => validateFn({
        options,
        property: {
            name: key,
            value: values[key]
        },
    }));
}

/**
 * This decorator verify that all parameters have the same type as the type
 * declared at design time. Optional parameters can be nil.
 *
 * **IMPORTANT**: In array types it does not check the elements type. It just checks if it is
 * instance of Array or not
 * @param options optional parameters indexes
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` js
 * class Test () {
 *  @validateParameters({ optionalIndexes: [1, 2] })
 *   test1(str: string, num?: number, arr?: number[]) {
 * }
 * ```
 */
function validateParameters(options) {
    return (target, propertyName, descriptor) => {
        const method = descriptor.value;
        const paramsTypes = Reflect.getMetadata('design:paramtypes', target, propertyName);
        const optionalParams = options ? options.optionalIndexes : [];
        const isMandatoryParam = (index) => {
            return not(optionalParams.find(val => val === index));
        };
        const validateParam = (arg, index) => {
            const primitiveParamConstraints = allPass(isPrimitive, isTypeOf(paramsTypes[index].name.toLowerCase()));
            const noPrimitiveParamConstraints = allPass(complement(isPrimitive), arg => arg instanceof paramsTypes[index]);
            const paramConstraints = anyPass(primitiveParamConstraints, noPrimitiveParamConstraints);
            return paramConstraints(arg);
        };
        // new method value
        descriptor.value = function (...args) {
            for (let i = 0; i < paramsTypes.length; i = i + 1) {
                const mandatoryParamsConstraints = allPass(isMandatoryParam, (index) => validateParam(args[index], index));
                const optionalParamsConstraints = allPass(complement(isMandatoryParam), anyPass((i) => isNil(args[i]), (index) => validateParam(args[index], index)));
                const constraints = anyPass(mandatoryParamsConstraints, optionalParamsConstraints);
                if (constraints(i)) {
                    continue;
                }
                throw new DwError(`Method ${propertyName}. The type on parameter with index ${i} is not right`, ErrorTypes.DW_VALIDATORS, ValidatableErrorCodes.DW_INVALID_PARAMETER_TYPE);
            }
            if (method) {
                return method.apply(this, args);
            }
        };
    };
}

/**
 * Levels for logging messages
 */
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARNING"] = 2] = "WARNING";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
    LogLevel[LogLevel["NONE"] = 4] = "NONE";
})(LogLevel || (LogLevel = {}));

/**
 * Base class for the properties the modules needs.
 * When a module inherits from configurable this class is needed.
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * class SecurityConfigProps extends BaseConfigProp implements SecurityJsonProps {
 * // ...
 * }
 * ```
 */
class BaseConfigProps extends validatable() {
    constructor(arg) {
        super(arg);
        this.logLevel = isNil(this.logLevel) ? LogLevel.ERROR : this.logLevel;
        this.language = this.language || 'es_ES';
    }
}
__decorate([
    stringProperty(),
    requiredProperty(),
    __metadata("design:type", String)
], BaseConfigProps.prototype, "appKey", void 0);
__decorate([
    stringProperty({ minLength: 3 }),
    requiredProperty(),
    __metadata("design:type", String)
], BaseConfigProps.prototype, "appName", void 0);
__decorate([
    stringProperty({ regExp: /^([a-z]{2}[a-z]?_[A-Z]{2}|eo)$/ }),
    __metadata("design:type", String)
], BaseConfigProps.prototype, "language", void 0);
__decorate([
    numberProperty({ min: 0, max: 4 }),
    __metadata("design:type", Number)
], BaseConfigProps.prototype, "logLevel", void 0);

/**
 * Used by the method createConfigProps from each module
 * when these inherit from BaseConfigProps.
 * Properties needed by each module.
 */
const MANDATORY_BASE_CONFIG_PROPS = ['appName', 'appKey'];

/**
 * Common events for all Darwin modules
 */
var CommonEvents;
(function (CommonEvents) {
    CommonEvents["LOG"] = "log";
    CommonEvents["ERROR"] = "error";
})(CommonEvents || (CommonEvents = {}));

const getDwVersion = () => '2.5.0';

/**
 * Returns the value from the given cookie name if it exists, otherwise it returns '' (empty string).
 * @param name  Cookie name
 * @usageNotes How to get a cookie effortlessly.
 * ### Example
 * ``` typescript
 * // Cookies  'DISD=firstValueCookie;LSID=DQAAAK…Eaem_vYg;another_cookie=test;'
 * getCookie('DISD') // firstValueCookie
 *
 * ```
 */
function getCookie(name) {
    let value;
    const tmp = document.cookie.match(`(?:^|;)\\s*${name}\\s*=\\s*([^;]+)`);
    if (tmp) {
        value = tmp.pop();
    }
    return value ? value : '';
}

/**
 * CSRF / XSRF Headers name
 */
const XSRF_HEADER_NAME = 'x-xsrf-token';
/**
 * CSRF / XSRF Cookie name
 */
const XSRF_COOKIE_NAME = 'XSRF-TOKEN';

/**
 * Takes an Options from the fetch API & adds
 * the XSRF Header if the XSRF cookie its present.
 * @param init Object used by fetch to set add options
 * @usageNotes How to add the XSRF Header if needed.
 * ### example
 * ``` ts
 *  // Suppose you have these cookies:
 *  // XSRF-TOKEN=test;made_write_conn=1295214458; Path=/; Domain=.example.com'
 *  const authHeaders = new Headers({'Authorization', 'Bearer test'})};
 *  const xsrfAuthHeaders = addXsrfHeader({ headers: authHeaders });
 *  // xsrfAuthHeaders = { headers: Headers } -- The object Headers has:
 *  // - 'Authorization' whose value is 'Bearer test'.
 *  // - 'x-xsrf-token' whose value is 'test'.
 * ```
 */
const addXsrfHeader = (init) => {
    const xsrfCookie = getCookie(XSRF_COOKIE_NAME);
    if (not(isEmpty(xsrfCookie))) {
        const headers = new Headers(init && init.headers);
        headers.append(XSRF_HEADER_NAME, xsrfCookie);
        return Object.assign({}, init, { headers });
    }
    return Object.assign({}, init);
};

function fetchRequest(input, init) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(input, addXsrfHeader(init));
            const { ok, status, statusText } = response;
            // 'ok' property is true if response.status: 200-299
            if (ok && status !== 204) {
                return response.json();
            }
            if (!ok) {
                throw new DwError(statusText, ErrorTypes.DW_FETCH, status);
            }
            // response status is 204 (empty body)
            return;
        }
        catch (err) {
            // tslint:disable-next-line:max-line-length
            // https://stackoverflow.com/questions/33870684/why-doesnt-instanceof-work-on-instances-of-error-subclasses-under-babel-node/33877501#33877501
            if (err instanceof DwError) {
                throw err;
            }
            const { message, name = ErrorTypes.DW_FETCH, code } = err;
            throw new DwError(message, name, code);
        }
    });
}

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 * @param func
 * @param wait milliseconds delay
 * @param immediate false by default
 */
function debounce(fn, wait, immediate = false) {
    let timeout;
    function debounced() {
        const args = arguments;
        const later = () => {
            timeout = null;
            if (!immediate) {
                fn.apply(this, args);
            }
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) {
            fn.apply(this, args);
        }
    }
    debounced.clear = function () {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    };
    return debounced;
}

/**
 * Function to remove a cookie.
 * @param name Cookie name
 * @usageNotes How to remove a cookie.
 * ### Example
 * ``` typescript
 * // Cookies  'DISD=firstValueCookie;LSID=DQAAAEaem_vYg;another_cookie=test;'
 * removeCookie('DISD') // Remove DisD=firstValueCookie
 *
 * ```
 */
function removeCookie(name) {
    return document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}

/**
 * Error codes for dw-emittable
 */
var EmittableErrorCodes;
(function (EmittableErrorCodes) {
    EmittableErrorCodes[EmittableErrorCodes["DW_INVALID_LOG_LEVEL"] = 1601] = "DW_INVALID_LOG_LEVEL";
})(EmittableErrorCodes || (EmittableErrorCodes = {}));

/**
 * Event emitter class that manage an array of events and store callbacks to emit
 */
class EventEmitter {
    constructor() {
        this.events = {};
    }
    /**
     * Subscribe function to store callback on event
     * @param eventName key name for store event
     * @param listener callback to execute when is emitted
     */
    on(eventName, listener) {
        let listeners = this.events[eventName];
        if (!this.events[eventName]) {
            listeners = new Set();
            this.events[eventName] = listeners;
        }
        listeners.add(listener);
        return () => this.off(eventName, listener);
    }
    /**
     * Unsubscribe a callback on a event
     * @param eventName key name for store event
     * @param listener callback to execute when is emitted
     */
    off(eventName, listener) {
        if (this.events[eventName]) {
            this.events[eventName].delete(listener);
        }
    }
    /**
     * Execute all callbacks on a event
     * @param eventKey key name of event
     * @param event item to send to callbacks
     */
    emit(event, payload) {
        const listeners = this.events[event];
        if (this.events[event]) {
            listeners.forEach(listener => listener(payload, event));
        }
    }
}

/**
 * Event emitter that manages log and error events
 */
class DwEmitter extends EventEmitter {
    /**
     * Emits a log event only on debug level
     * @param msg message to be emitted
     */
    emitLogDebug(msg) {
        if (mustBeEmitted(this.logLevel, LogLevel.DEBUG)) {
            this.emitLog(msg, LogLevel.DEBUG);
        }
    }
    /**
     * Emits a log event on Debug or Info level
     * @param msg message to be emitted
     */
    emitLogInfo(msg) {
        if (mustBeEmitted(this.logLevel, LogLevel.INFO)) {
            this.emitLog(msg, LogLevel.INFO);
        }
    }
    /**
     * Emits a log event on Debug or Info or Warning level
     * @param msg message to be emitted
     */
    emitLogWarning(msg) {
        if (mustBeEmitted(this.logLevel, LogLevel.WARNING)) {
            this.emitLog(msg, LogLevel.WARNING);
        }
    }
    /**
     * Emits a log event on Debug or Info or Warning or Error level
     * @param msg message to be emitted
     */
    emitLogError(msg) {
        if (mustBeEmitted(this.logLevel, LogLevel.ERROR)) {
            this.emitLog(msg, LogLevel.ERROR);
        }
    }
    /**
     * Emits an Error event. Also emits a Log event depending on the configured loglevel
     * @param error error instance to be emitted
     */
    emitError(error) {
        this.emit(CommonEvents.ERROR, error);
        this.emitLogError(error.message);
    }
    /**
     * Emits log
     * @param msg message to emit
     * @param logLevel log level to emit
     */
    emitLog(msg, msglevelType) {
        this.emit(CommonEvents.LOG, {
            level: LogLevel[msglevelType],
            msg: `${msg}`,
        });
    }
}
/**
 * Check if message must be emitted depending on the evel configuration
 * and message level
 * @param configLevel current level of priority
 * @param msgLevel message level
 */
function mustBeEmitted(configLevel, msgLevel) {
    if (isNil(configLevel)) {
        throw new DwError('You must provide a valid logLevel', ErrorTypes.DW_EMITTER, EmittableErrorCodes.DW_INVALID_LOG_LEVEL);
    }
    return configLevel <= msgLevel;
}

/* tslint:disable:variable-name */
/**
 * Mixin to add a base class the feature of emitting events
 *
 * @param Base base class which de feature will be added
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * // Library Definition file
 * class LibraryEmittable extends dwEmittable<'libraryCustomEvent'>()() {
 *  constructor(private config:LibraryConfigProps) {
 *       super();
 *       this._dwEmitter.logLevel = config.logLevel;
 *  }
 *  method() {
 *    this._dwEmitter.emit('libraryCustomEvent','payload')
 *  }
 * }
 * // Usage
 * const config:any = { logLevel: 2 };
 * const libraryConfigProps:LibraryConfigProps = new LibraryConfigProps(config);
 * const library = new LibraryEmittable(libraryConfigProps);
 * const callback = (e) => console.log(e);
 * library.on('libraryCustomEvent', callback)
 * ```
 */
function dwEmittable() {
    return function (Base = class {
    }) {
        return class extends Base {
            constructor() {
                super(...arguments);
                /**
                 * emitter property through which events will be emitted
                 */
                this._dwEmitter = new DwEmitter();
            }
            /**
             * Subscribe method to store callback on event
             * @param eventName key name for store event
             * @param listener callback to execute when the event is emitted
             */
            on(eventName, listener) {
                return this._dwEmitter.on(eventName, listener);
            }
        };
    };
}

/**
 * Error codes for headerable
 */
var HeaderableErrorCodes;
(function (HeaderableErrorCodes) {
    HeaderableErrorCodes[HeaderableErrorCodes["DW_INVALID_COMMON_HEADERS"] = 1501] = "DW_INVALID_COMMON_HEADERS";
    HeaderableErrorCodes[HeaderableErrorCodes["DW_INVALID_HEADERS"] = 1502] = "DW_INVALID_HEADERS";
    HeaderableErrorCodes[HeaderableErrorCodes["DW_INVALID_SECURITY_HEADERS"] = 1503] = "DW_INVALID_SECURITY_HEADERS";
})(HeaderableErrorCodes || (HeaderableErrorCodes = {}));

const securityHeadersKeys = [
    'Authorization',
    'X-Santander-Client-Id',
    'X-ClientId'
];
const commonHeadersKeys = [
    'App-Init',
    'Contact-Point',
    'Session-Id',
    'Organization',
];

const invalidHeadersMsg = 'Invalid Headers.';
const invalidCommonHeadersMsg = `Invalid Common Headers. It only accepts: ${commonHeadersKeys} Headers`;
const invalidSecurityHeadersMsg = `Invalid Security Headers. It only accepts: ${securityHeadersKeys} Headers`;

var HeaderMapKeyNames;
(function (HeaderMapKeyNames) {
    HeaderMapKeyNames["COMMON"] = "common";
    HeaderMapKeyNames["CUSTOM"] = "custom";
    HeaderMapKeyNames["SECURITY"] = "security";
})(HeaderMapKeyNames || (HeaderMapKeyNames = {}));

/* tslint:disable:variable-name function-name*/
/**
 * Mixin to provide headers properties to store a callback,
 * whose return value must be a Headers's instance
 * & and use it whenever a Http headers property is needed by the service.
 * @param Base base class
 * @usageNotes
 * How to properly extend from headerable
 * ### Example
 * ``` typescript
 * // Library Definition file
 * const myHeaders = new Headers({
 *  'Content-Type': 'text/xml'
 * });
 *
 * class LibraryCaller extends headerable() {
 *  constructor() {
 *    this._customHeaders = () => myHeaders;
 *  }
 * };
 *
 * // Usage
 * const libraryCaller = new LibraryCaller();
 * libraryCaller.customHeaders() // Headers
 * ```
 */
function headerable(Base = class {
}) {
    return class extends Base {
        constructor() {
            super(...arguments);
            this._headers = new Map();
        }
        /**
         * Private setter to store global headers. Only accepts
         * 'App-Init', 'Contact-Point', 'X-ClientId' & 'Session-Id' headers value,
         * otherwise it throws an exception
         *
         * @param headers Callback whose return its a headers's instance
         */
        set _commonHeaders(headers) {
            if (not(isValidCommonHeaders(headers))) {
                throw new DwError(invalidCommonHeadersMsg, ErrorTypes.DW_HEADERABLE, HeaderableErrorCodes.DW_INVALID_COMMON_HEADERS);
            }
            this._headers.set(HeaderMapKeyNames.COMMON, headers);
        }
        /**
         * Private setter to store any headers in case it's needed.
         *
         * @param headers Callback whose return its a headers's instance
         */
        set _customHeaders(headers) {
            if (not(isHeadersCallbackValid(headers))) {
                throw new DwError(invalidHeadersMsg, ErrorTypes.DW_HEADERABLE, HeaderableErrorCodes.DW_INVALID_HEADERS);
            }
            this._headers.set(HeaderMapKeyNames.CUSTOM, headers);
        }
        /**
         * Private setter to store the security headers in case a secured call is needed.
         * Only accepts 'Authorization' headers value, otherwise it throws an exception
         *
         * @param headers Callback whose return its a headers's instance
         */
        set _securityHeaders(headers) {
            if (not(isValidSecurityHeaders(headers))) {
                throw new DwError(invalidSecurityHeadersMsg, ErrorTypes.DW_HEADERABLE, HeaderableErrorCodes.DW_INVALID_SECURITY_HEADERS);
            }
            this._headers.set(HeaderMapKeyNames.SECURITY, headers);
        }
        /**
         * Getter to retrieve Common headers.
         */
        get commonHeaders() {
            return this._headers.get(HeaderMapKeyNames.COMMON) || (() => new Headers());
        }
        /**
         * Getter to retrieve Custom headers.
         */
        get customHeaders() {
            return this._headers.get(HeaderMapKeyNames.CUSTOM) || (() => new Headers());
        }
        /**
         * Getter to retrieve Security headers.
         */
        get securityHeaders() {
            return this._headers.get(HeaderMapKeyNames.SECURITY) || (() => new Headers());
        }
    };
}
const returnsHeaders = (headers) => headers() instanceof Headers;
const isHeadersCallbackValid = allPass(complement(isNil), isFunction, returnsHeaders);
const hasRequiredKeys = (a, b) => (a.every((aKey) => !!b.find((bKey) => aKey.toLowerCase() === bKey.toLowerCase())));
/**
 * It validates if the provided Headers keys match with an array of previously configured keys
 */
const areHeadersKeysValid = curry((keysToCompare, headersFn) => {
    const keys = Array.from(headersFn().keys());
    return hasRequiredKeys(keys, keysToCompare);
});
const createHeadersValidation = (valuesToCompare) => both(isHeadersCallbackValid, areHeadersKeysValid(valuesToCompare));
const isValidCommonHeaders = createHeadersValidation(commonHeadersKeys);
const isValidSecurityHeaders = createHeadersValidation(securityHeadersKeys);

/**
 * Its a function which allows to unify headers.
 * @param allHeaders HeadersCallback[]
 * @usageNotes
 * How to use it.
 * ### Example
 * ``` ts
 * const createMixHeaders = new Headers(mixHeaders(
 *   logHeadersCallback,
 *   customHeaderCallback,
 *   securityHeaderCallback,
 *   commonHeaderCallback
 * ));
 * ```
 */
const mixHeaders = (...allHeaders) => allHeaders
    .map((header) => Array.from(header().entries()))
    .reduce((prev, current) => [...prev, ...current], []);

/**
 * Error codes for configurable
 */
var ConfigurableErrorCodes;
(function (ConfigurableErrorCodes) {
    ConfigurableErrorCodes[ConfigurableErrorCodes["DW_INVALID_CONSTRUCTOR"] = 1401] = "DW_INVALID_CONSTRUCTOR";
    ConfigurableErrorCodes[ConfigurableErrorCodes["DW_INVALID_JSON_OBJECT"] = 1402] = "DW_INVALID_JSON_OBJECT";
    ConfigurableErrorCodes[ConfigurableErrorCodes["DW_MAIN_KEY_NOT_DEFINED"] = 1403] = "DW_MAIN_KEY_NOT_DEFINED";
    ConfigurableErrorCodes[ConfigurableErrorCodes["DW_INVALID_MAIN_KEY"] = 1404] = "DW_INVALID_MAIN_KEY";
    ConfigurableErrorCodes[ConfigurableErrorCodes["DW_INVALID_CREATE_CONFIG_PROPS_METHOD"] = 1405] = "DW_INVALID_CREATE_CONFIG_PROPS_METHOD";
})(ConfigurableErrorCodes || (ConfigurableErrorCodes = {}));

/**
 * Generic parser that allows to get any properties from an object and create an instance
 * of BaseConfigProps (or that inherits from it) with them
 * @param ConfigPropsConstructor constructor function to create an instance of
 * @param key Primary key (first level key) to get properties (and all subproperties)
 * @param otherskeys extra global properties to get (first level key)
 * @returns function which receives a json props and parse it to ConfigProps instance
 */
function configParser(
// tslint:disable-next-line:variable-name
ConfigPropsConstructor, key, otherskeys = []) {
    return (jsonConfig) => {
        // throw error if ConfigPropsConstructor is not a constructor
        if (ConfigPropsConstructor && typeof ConfigPropsConstructor !== 'function') {
            throw new DwError(
            // tslint:disable-next-line:max-line-length
            `A constructor function param was expected, and a ${typeof ConfigPropsConstructor} was received`, ErrorTypes.DW_CONFIG_PARSER, ConfigurableErrorCodes.DW_INVALID_CONSTRUCTOR);
        }
        // throw error if jsonConfig is not an object
        if (jsonConfig && jsonConfig.constructor !== {}.constructor) {
            throw new DwError(`A json object param was expected, and a ${typeof ConfigPropsConstructor} was received`, ErrorTypes.DW_CONFIG_PARSER, ConfigurableErrorCodes.DW_INVALID_JSON_OBJECT);
        }
        // throw error if the value of main key is not defined
        const { [key]: keyValue } = jsonConfig;
        if (typeof keyValue === 'undefined') {
            throw new DwError(`Primary key ${key} does not exist in the object`, ErrorTypes.DW_CONFIG_PARSER, ConfigurableErrorCodes.DW_MAIN_KEY_NOT_DEFINED);
        }
        // throw error if the value of main key is nos an object
        if (keyValue.constructor !== {}.constructor) {
            throw new DwError(`Primary key must be an object type and a ${typeof keyValue} was received`, ErrorTypes.DW_CONFIG_PARSER, ConfigurableErrorCodes.DW_INVALID_MAIN_KEY);
        }
        // assigning of main key
        // @ts-ignore
        const parseredObj = Object.assign({}, jsonConfig[key]);
        // assinging of secondary keys
        let otherKeyGlobalValue;
        let otherKeyLocalValue;
        otherskeys.forEach((otherKey) => {
            // it gets the value of the others keys inside global json as primary key
            // and inside other primary keys as well (this local value has priority)
            ({ [otherKey]: otherKeyGlobalValue } = jsonConfig);
            ({ [otherKey]: otherKeyLocalValue } = (jsonConfig[key]));
            // if local value does not exist but the global one does, it copies the global value
            // otherwise local has priority
            if (typeof otherKeyLocalValue === 'undefined' &&
                typeof otherKeyGlobalValue !== 'undefined') {
                Object.assign(parseredObj, { [otherKey]: otherKeyGlobalValue });
            }
        });
        return new ConfigPropsConstructor(parseredObj);
    };
}

/* tslint:disable:variable-name */
/**
 * Mixin to add a static method config to the base class
 *
 * @param Base base class
 * @usageNotes
 * How to use it.
 * ### Example 1
 * ``` ts
 * // Promises
 * // Definition file
 * class LibraryConfig extends configurable<LibraryConfigProps>()() {
 *
 *    public static createConfigProps = configParser<LibraryConfigProps>(
 *       LibraryConfigProps,
 *       'library',
 *       ['logLevel', 'appName']
 *     );
 *   }
 *
 *   // Usage
 *   const instanceLibrary = (config) => {
 *     const libraryConfigProps:LibraryConfigProps = LibraryConfig.createConfigProps(config.config);
 *     const libraryConfig:LibraryConfig = new LibraryConfig(libraryConfigProps);
 *   };
 *
 *   const config = new Config();
 *   config.load().then(instanceLibrary);
 * ```
 * ### Example 2
 * ``` ts
 *   // Async/await
 *   // Definition file
 *   class LibraryConfig extends configurable<LibraryConfigProps>()() {
 *
 *   public static createConfigProps = configParser<LibraryConfigProps>(
 *     LibraryConfigProps,
 *     'library',
 *     ['logLevel', 'appName']
 *   );
 * }
 *  // Usage IIFE
 *   (async () => {
 *     const config = new Config();
 *     await config.load();
 *     const libraryConfigProps:LibraryConfigProps = LibraryConfig.createConfigProps(config.config);
 *     const libraryConfig:LibraryConfig = new LibraryConfig(libraryConfigProps);
 *   })();
 * ```
 */
function configurable() {
    return function (Base = class {
    }) {
        return class extends Base {
            /**
             * Function that must return an instance of BaseConfigProps the module/class needs
             * It has to be overriden by the child class
             * @param jsonConfig
             */
            static createConfigProps(jsonConfig) {
                throw new DwError('config function has to be override by child class', ErrorTypes.DW_CONFIGURABLE, ConfigurableErrorCodes.DW_INVALID_CREATE_CONFIG_PROPS_METHOD);
            }
        };
    };
}

const applePortableDevices = ['iPhone', 'iPad', 'iPod'];
const consoleDevices = ['archos gamepad', 'nintendo', 'nitro', 'shield build', 'playstation', 'ouya', 'xbox'];
const tabletDevices = /ipad|(android(?!.*mobile))|xoom|sch-i800|playbook|tablet|kindle/i;
const mobileLikeDevices = /iphone|ipod|(android(.*mobile))|blackberry|opera mini|windows\sce|palm|smartphone|iemobile/i;

const ANDROID = 'Android';
const LINUX = 'Linux';
const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
const osNonPortableDevices = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];

const isMac = ({ platform }) => osNonPortableDevices.includes(platform);
const isIOs = ({ platform }) => applePortableDevices.includes(platform);
const isWindows = ({ platform }) => windowsPlatforms.includes(platform);
const isAndroid = ({ ua }) => ua.includes(ANDROID);
const isLinux = ({ platform }) => platform.includes(LINUX);
/**
* Obtain he operating system that the user of the application is using
* @param browserInfo
*/
const getOs = cond([
    [isAndroid, always('android')],
    [isWindows, always('windows')],
    [isLinux, always('linux')],
    [isMac, always('macos')],
    [isIOs, always('ios')],
    [T, always('other')]
]);

const isMobile = ({ ua }) => mobileLikeDevices.test(ua);
const isTablet = ({ ua }) => tabletDevices.test(ua);
const isConsole = ({ ua }) => consoleDevices.includes(ua);
const isWindowsDesktop = both(isWindows, complement(isMobile));
const isLinuxDesktop = both(isLinux, complement(isAndroid));
const isAnyDesktop = anyPass(isWindowsDesktop, isMac, isLinuxDesktop);
const isNotDevice = complement(both(isAndroid, isConsole));
const isDesktop = both(isAnyDesktop, isNotDevice);
/**
* Obtain the device type.
* @param browserInfo
*/
const getDevice = cond([
    [isMobile, always('mobile')],
    [isTablet, always('tablet')],
    [isDesktop, always('desktop')],
    [T, always('other')]
]);

const browsersRegex = (/(chrome|opera|edge|safari|firefox|msie|brave|vivaldi|playstation|(=\/))\/?\s*(\d+)/i);
const ieVersionRegex = /(\brv[ :]+|ie )(\d+)/i;
const ieRegex = /trident/i;
const edgeRegex = /\b(edge|edg|edga)\/(\d+)/i;
const operaRegex = /\b(opr)\/(\d+)/i;
const vivaldiRegex = /\b(vivaldi)\/(\d+)/i;
const braveRegex = /\b(brave)\/(\d+)/i;
const safariRegex = /version\/(\d+).*(safari)|(safari)/i;

/**
 * Predicate that test the given regular expression against the userAgent.
 */
const testRegex = curry((regex, userAgent) => regex.test(userAgent));
const createBrowserInfo = (browser, version) => ({ browser, version });
const isChrome = (userAgent) => userAgent.toLowerCase().includes('chrome');
const isSafari = both(complement(isChrome), testRegex(safariRegex));
const createSafari = (userAgent) => {
    const version = userAgent.match(safariRegex)[1];
    return createBrowserInfo('Safari', version || 'unknown');
};
const restNameVersion = ifElse(isEmpty, always(['unknown', 'unknown']), ([nameVersion]) => nameVersion.split('/'));
/**
 * Creates an object based in createBrowserInfo object.
 * Its used when the browser's UserAgent has other browser's names
 * and its confusing to retrieve the right browser name.
 *
 * @param name Browser's name
 * @param regExp RegExp to retrieve the version
 * @param userAgent
 */
const createSpecific = curry((name, regExp, userAgent) => (createBrowserInfo(name, userAgent.match(regExp)[2])));
/**
 * Creates an object based in createBrowserInfo object.
 * The browser's name has to be listed within the constant browsersRegex
 * in order for this to work, otherwise, it returns unknown
 */
const createRest = (userAgent) => {
    const [name, version] = restNameVersion(userAgent.match(browsersRegex));
    return createBrowserInfo(name, version || 'unknown');
};
/**
 * Finds out which browser and version the user is using.
 */
const getBrowserInfo = cond([
    [testRegex(ieRegex), createSpecific('IE', ieVersionRegex)],
    [testRegex(operaRegex), createSpecific('Opera', operaRegex)],
    [testRegex(edgeRegex), createSpecific('Edge', edgeRegex)],
    [testRegex(vivaldiRegex), createSpecific('Vivaldi', vivaldiRegex)],
    [testRegex(braveRegex), createSpecific('Brave', braveRegex)],
    [isSafari, createSafari],
    [T, createRest]
]);

export { BaseConfigProps, CommonEvents, ConfigurableErrorCodes, DwEmitter, DwError, EmittableErrorCodes, ErrorTypes, F, HeaderableErrorCodes, LogLevel, MANDATORY_BASE_CONFIG_PROPS, StringTypeValidator, T, ValidatableErrorCodes, __, allPass, always, and, anyPass, assoc, assocMutate, booleanProperty, both, complement, cond, configParser, configurable, curry, debounce, dwEmittable, either, eqProps, equals, fetchRequest, getBrowserInfo, getCookie, getDevice, getDwVersion, getOs, gt, gte, headerable, identity, ifElse, isArray, isDate, isEmpty, isFunction, isNil, isPrimitive, isTypeOf, lt, lte, mixHeaders, not, numberProperty, or, pipe, removeCookie, requiredProperty, stringProperty, take, tap, trace, unless, validatable, validateParameters, when, where, whereEq };
//# sourceMappingURL=common.js.map
